import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Wallet, ExternalLink, LogOut, Copy, Check, Coins, ArrowRight, AlertTriangle, ChevronDown, Settings, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortfolioValue } from '@/hooks/usePortfolioValue';
import { formatCreatorReward } from '@/lib/meteora/feeMetricsClient';
import { useCreatorFeesQuery } from '@/hooks/useCreatorFeesQuery';
import { useSOLPrice } from '@/hooks/useSOLPrice';
import { queryKeys } from '@/lib/queryClient';
import { getTurnkeyMissingKeys } from '@/config/turnkeyPublicConfig';
import { WalletSwitcher } from './WalletSwitcher';
import { WalletModal } from './WalletModal';
import { useCreatorDisplayName } from '@/hooks/useCreatorDisplayName';
import { useSessionExpiryContextSafe } from '@/providers/SessionExpiryProvider';
import { useIsMobile } from '@/hooks/use-mobile';

export const WalletButton: FC = () => {
  const { 
    publicKey, 
    connected, 
    logout, 
    login, 
    walletAddress, 
    isConfigured, 
    isLoading,
    allSolanaWallets,
    activeWalletIndex,
  } = useTurnkeyWallet();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Portfolio total USD value (SOL + all SPL tokens)
  const { formattedTotalUsd, isLoading: balanceLoading, error: balanceError } = usePortfolioValue();

  // Creator rewards (cached via React Query - starts fetching at page load)
  const { totalClaimable, isLoading: creatorFeesLoading, isError: creatorFeesError } = useCreatorFeesQuery(connected ? walletAddress : null);
  const { data: solPriceData } = useSOLPrice();
  const formattedRewards = formatCreatorReward(totalClaimable, solPriceData?.price ?? null);

  // Creator profile for username display + avatar
  const { displayName, isLoading: profileLoading, avatarUrl, avatarPath, avatarVersion } = useCreatorDisplayName(walletAddress);
  const isMobile = useIsMobile();

  // Session expiry context for marking intentional logout
  const sessionExpiryContext = useSessionExpiryContextSafe();

  const shortAddress = walletAddress
    ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`
    : '';
    
  const hasMultipleWallets = allSolanaWallets.length > 1;
  const activeWallet = allSolanaWallets[activeWalletIndex];

  // Listen for "Deposit SOL" action from insufficient-SOL toast notifications
  useEffect(() => {
    const handleOpenDeposit = () => setWalletModalOpen(true);
    window.addEventListener('memelaunch:open-wallet-deposit', handleOpenDeposit);
    return () => window.removeEventListener('memelaunch:open-wallet-deposit', handleOpenDeposit);
  }, []);

  // Force fresh data for both portfolio value and creator fees when dropdown opens
  useEffect(() => {
    if (dropdownOpen && connected && walletAddress) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallet.portfolioValue(walletAddress),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.creators.fees(walletAddress),
        refetchType: 'active',
      });
    }
  }, [dropdownOpen, connected, walletAddress, queryClient]);

  // Brief "loaded" checkmark indicator
  const [showFreshCheck, setShowFreshCheck] = useState(false);
  const allDataLoaded = !balanceLoading && !creatorFeesLoading;

  useEffect(() => {
    if (allDataLoaded && dropdownOpen) {
      setShowFreshCheck(true);
      const timer = setTimeout(() => setShowFreshCheck(false), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowFreshCheck(false);
    }
  }, [allDataLoaded, dropdownOpen]);

  const handleConnect = () => {
    login();
  };

  const handleCopy = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleViewExplorer = () => {
    if (walletAddress) {
      const isDevnet = import.meta.env.VITE_USE_DEVNET === 'true';
      const cluster = isDevnet ? '?cluster=devnet' : '';
      window.open(`https://solscan.io/account/${walletAddress}${cluster}`, '_blank');
    }
  };

  const handleClaimFees = () => {
    setDropdownOpen(false);
    navigate('/creator/fees');
  };

  const handleViewPortfolio = () => {
    setDropdownOpen(false);
    navigate('/portfolio');
  };

  const handleViewWallet = () => {
    setDropdownOpen(false);
    setWalletModalOpen(true);
  };

  const handleProfile = () => {
    setDropdownOpen(false);
    navigate(`/creator/${walletAddress}`);
  };

  const handleSettings = () => {
    setDropdownOpen(false);
    navigate('/settings');
  };

  const handleOpenDiagnostics = () => {
    navigate('/auth/diagnostics');
  };

  // Show skeleton while auth is resolving (prevents flash of sign-in button)
  if (isLoading && !connected) {
    return <Skeleton className="h-11 md:h-9 w-20 rounded-md" />;
  }

  if (!connected) {
    // Show disabled state if Turnkey is not configured
    if (!isConfigured) {
      const missingKeys = getTurnkeyMissingKeys();
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenDiagnostics}
          className="h-11 md:h-9 gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
        >
          <AlertTriangle className="w-4 h-4" />
          Sign In unavailable
        </Button>
      );
    }
    
    return (
      <Button
        onClick={handleConnect}
        disabled={isLoading}
        size="sm"
        className="h-11 md:h-9"
      >
        {isLoading ? 'Loading...' : 'Sign In'}
      </Button>
    );
  }

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        {isMobile ? (
          <button className="flex-shrink-0">
            <UserAvatar
              avatarUrl={avatarUrl}
              avatarPath={avatarPath}
              avatarVersion={avatarVersion}
              alt="Profile"
              size="sm"
              className="h-11 w-11 avatar-border-glow"
            />
          </button>
        ) : (
          <Button variant="outline" className="gap-2 pl-1.5">
            <UserAvatar
              avatarUrl={avatarUrl}
              avatarPath={avatarPath}
              avatarVersion={avatarVersion}
              size="sm"
              className="avatar-border-glow"
            />
            {/* Username */}
            <span className="flex items-center gap-1">
              {profileLoading ? shortAddress : ((displayName || shortAddress).replace(/^@/, ''))}
              {hasMultipleWallets && (
                <span className="text-xs text-muted-foreground">
                  ({activeWalletIndex + 1}/{allSolanaWallets.length})
                </span>
              )}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 md:w-72">
        {/* Wallet Switcher - only show if multiple wallets */}
        {hasMultipleWallets && (
          <>
            <WalletSwitcher onSelect={() => {}} />
            <DropdownMenuSeparator />
          </>
        )}
        
        <DropdownMenuItem onClick={handleProfile} className="gap-2 cursor-pointer">
          <User className="w-4 h-4" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleCopy} className="gap-2 cursor-pointer">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Address'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleViewExplorer} className="gap-2 cursor-pointer">
          <ExternalLink className="w-4 h-4" />
          View on Solscan
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Portfolio Balance Section */}
        <div className="px-2 py-2 bg-muted/30 rounded-md mx-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Wallet className="w-4 h-4 text-primary" />
              <span className="text-foreground font-medium">Total</span>
            </div>
            <div className="flex items-center gap-1">
              {balanceLoading ? (
                <Skeleton className="h-4 w-14" />
              ) : balanceError ? (
                <span className="text-xs text-muted-foreground">–</span>
              ) : (
                <span className="text-sm font-bold text-foreground flex items-center gap-1 animate-content-fade-in">
                  {formattedTotalUsd ?? '$0.00'}
                  {showFreshCheck && <Check className="w-3 h-3 text-green-500" />}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1.5 justify-between"
            onClick={handleViewPortfolio}
          >
            <span>View portfolio</span>
            <ArrowRight className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1 justify-between"
            onClick={handleViewWallet}
          >
            <span>View wallet</span>
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Creator Rewards Row */}
        <div className="px-2 py-2 bg-muted/30 rounded-md mx-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Coins className="w-4 h-4 text-primary" />
              <span className="text-foreground font-medium">Creator rewards</span>
            </div>
            <div className="flex items-center gap-1">
              {creatorFeesLoading ? (
                <Skeleton className="h-4 w-14" />
              ) : creatorFeesError ? (
                <span className="text-xs text-muted-foreground">–</span>
              ) : (
                <span className="text-sm font-bold text-foreground flex items-center gap-1 animate-content-fade-in">
                  {formattedRewards.usdValue}
                  {showFreshCheck && <Check className="w-3 h-3 text-green-500" />}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1.5 justify-between"
            onClick={handleClaimFees}
          >
            <span>{!totalClaimable.isZero() ? 'Claim rewards' : 'View details'}</span>
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSettings} className="gap-2 cursor-pointer">
          <Settings className="w-4 h-4" />
          Settings
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => {
            // Mark as intentional logout to prevent session expired modal
            sessionExpiryContext?.markIntentionalLogout();
            logout();
          }}
          className="gap-2 cursor-pointer text-destructive"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* Wallet Modal for withdraw/export */}
      <WalletModal open={walletModalOpen} onOpenChange={setWalletModalOpen} />
    </DropdownMenu>
  );
};

export default WalletButton;
